import React, { useState, useEffect } from 'react';
import { BarChart3, Users, Eye, Calendar, RefreshCw, Lock, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [credentials, setCredentials] = useState({ username: '', password: '' });

  // Check if already logged in
  useEffect(() => {
    const token = sessionStorage.getItem('stats_token');
    if (token) {
      setIsLoggedIn(true);
      fetchStats();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
      
      const data = await response.json();
      
      if (data.success) {
        sessionStorage.setItem('stats_token', data.token);
        setIsLoggedIn(true);
        fetchStats();
      } else {
        setLoginError('Ongeldige gebruikersnaam of wachtwoord');
      }
    } catch (error) {
      setLoginError('Er is een fout opgetreden');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('stats_token');
    setIsLoggedIn(false);
    setStats(null);
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
    setLoading(false);
  };

  // Login Form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-dark to-primary-blue flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="bg-primary-green/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-primary-green" />
            </div>
            <h1 className="text-2xl font-bold text-primary-dark">Statistieken</h1>
            <p className="text-gray-500">Kine Julien Ryckeboer</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gebruikersnaam
              </label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                placeholder="Gebruikersnaam"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Wachtwoord
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                placeholder="••••••••"
                required
              />
            </div>
            
            {loginError && (
              <div className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm">
                {loginError}
              </div>
            )}
            
            <button
              type="submit"
              className="w-full bg-primary-green hover:bg-primary-green-dark text-white font-medium py-3 rounded-lg transition-colors"
            >
              Inloggen
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <a href="/" className="text-primary-blue hover:underline text-sm">
              ← Terug naar website
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-green"></div>
      </div>
    );
  }

  const maxVisits = Math.max(...(stats?.last_7_days?.map(d => d.visits) || [1]), 1);

  // Stats Dashboard
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-primary-dark">Website Statistieken</h1>
            <p className="text-gray-500">Kine Julien Ryckeboer</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchStats}
              className="flex items-center gap-2 bg-primary-green text-white px-4 py-2 rounded-lg hover:bg-primary-green-dark transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Vernieuwen
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Uitloggen
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="bg-primary-green/10 p-3 rounded-lg">
                <Eye className="h-6 w-6 text-primary-green" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Totaal Bezoeken</p>
                <p className="text-3xl font-bold text-primary-dark">{stats?.total_visits || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="bg-primary-blue/10 p-3 rounded-lg">
                <Users className="h-6 w-6 text-primary-blue" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Unieke Bezoekers</p>
                <p className="text-3xl font-bold text-primary-dark">{stats?.unique_visitors || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="bg-amber-100 p-3 rounded-lg">
                <Calendar className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Vandaag</p>
                <p className="text-3xl font-bold text-primary-dark">{stats?.today_visits || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Last 7 Days Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="h-5 w-5 text-primary-green" />
            <h2 className="text-lg font-semibold text-primary-dark">Laatste 7 Dagen</h2>
          </div>
          
          <div className="flex items-end justify-between gap-2 h-48">
            {stats?.last_7_days?.slice().reverse().map((day, index) => {
              const height = day.visits > 0 ? (day.visits / maxVisits) * 100 : 5;
              const date = new Date(day.date);
              const dayName = date.toLocaleDateString('nl-BE', { weekday: 'short' });
              
              return (
                <div key={index} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-sm font-medium text-primary-dark">{day.visits}</span>
                  <div 
                    className="w-full bg-gradient-to-t from-primary-green to-primary-blue rounded-t-lg transition-all duration-500"
                    style={{ height: `${height}%`, minHeight: '8px' }}
                  ></div>
                  <span className="text-xs text-gray-500">{dayName}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Last Visit */}
        {stats?.last_visit && (
          <div className="mt-6 text-center text-gray-500 text-sm">
            Laatste bezoek: {new Date(stats.last_visit).toLocaleString('nl-BE')}
          </div>
        )}

        {/* Back link */}
        <div className="mt-8 text-center">
          <a href="/" className="text-primary-blue hover:underline">
            ← Terug naar website
          </a>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
